import { Controller } from '@hotwired/stimulus';

// 労働保険 算定基礎賃金 申告書の計算機(旧 legacy/companies/lias.js)。
// #form_labour_insurance_application 内の入力から確定/概算保険料・充当/不足/還付・
// 期別納付額を再計算し、全角→半角正規化とカンマ整形を行う。変更のたびに
// fetch で保存する(旧実装は remote:true フォームの $.submit。レスポンスは
// "true"/"false" のみで JS は参照しないため fire-and-forget)。
export default class extends Controller {
  // 旧 lias.js の document 委譲ハンドラをフォームスコープの委譲で再現する。
  // 再計算のトリガは特定フィールド(.lias-ratio と個別 id)+ #ennou change に限定
  // (全 input で発火させると入力途中にカンマ整形が走りカーソルが乱れるため)。
  static RECALC_KEYUP_IDS = ['kakutei11', 'kakutei41', 'kakutei51', 'gaisan11', 'gaisan41', 'hokenryo'];

  connect() {
    this.isComposing = false;
    this.onCompositionStart = (ev) => {
      if (this.isTextInput(ev.target)) this.isComposing = true;
    };
    this.onCompositionEnd = (ev) => {
      if (!this.isTextInput(ev.target)) return;
      this.isComposing = false;
      this.recalc();
    };
    this.onKeyup = (ev) => {
      if (this.isComposing) return;
      const t = ev.target;
      if (t.classList.contains('lias-ratio') || this.constructor.RECALC_KEYUP_IDS.includes(t.id)) {
        this.recalc();
      }
    };
    this.onChange = (ev) => {
      if (ev.target.id === 'ennou') this.recalc();
    };

    this.element.addEventListener('compositionstart', this.onCompositionStart);
    this.element.addEventListener('compositionend', this.onCompositionEnd);
    this.element.addEventListener('keyup', this.onKeyup);
    this.element.addEventListener('change', this.onChange);

    this.recalc();
  }

  disconnect() {
    this.element.removeEventListener('compositionstart', this.onCompositionStart);
    this.element.removeEventListener('compositionend', this.onCompositionEnd);
    this.element.removeEventListener('keyup', this.onKeyup);
    this.element.removeEventListener('change', this.onChange);
  }

  isTextInput(target) {
    return target && target.matches && target.matches('input[type=text]');
  }

  el(id) {
    return this.element.querySelector(`#${id}`);
  }

  v(id) {
    const el = id && this.el(id);
    if (el && el.value !== undefined) {
      return parseFloat(el.value.replace(/,/g, '')) * 1.0;
    }
    return NaN;
  }

  set(id, value) {
    const el = this.el(id);
    if (el) el.value = value;
  }

  recalc() {
    const v = (id) => this.v(id);
    const set = (id, value) => this.set(id, value);

    set('value10_h1_a1', v('value10_h1') * v('rosai_ha'));
    set('value10_h2_a1', v('value10_h2') * v('rosai_ha'));
    set('value12_h1_a1', v('value12_h1') * v('koyo_chi'));
    set('value12_h2_a1', v('value12_h2') * v('koyo_ri'));
    set('value10_a1', v('value10_h1_a1') + v('value10_h2_a1'));
    set('value12_a1', v('value12_h1_a1') + v('value12_h2_a1'));

    if (
      v('value10_a1') !== Math.floor(v('value10_a1')) &&
      v('value10_h1') === v('value12_h1') &&
      v('value10_h2') === v('value12_h2') &&
      v('value10_a1') - Math.floor(v('value10_a1')) + (v('value12_a1') - Math.floor(v('value12_a1'))) > 1
    ) {
      set(
        'value10_a2',
        Math.floor(
          v('value10_a1') +
            (v('value10_a1') - Math.floor(v('value10_a1'))) +
            (v('value12_a1') - Math.floor(v('value12_a1')))
        )
      );
    } else {
      set('value10_a2', Math.floor(v('value10_a1')));
    }
    set('value12_a2', Math.floor(v('value12_a1')));

    set('kakutei12', v('value10_a2'));
    set('kakutei42', v('value12_a2'));
    set('kakutei02', v('kakutei12') + v('kakutei42'));

    set('kakutei01', v('kakutei11') + v('kakutei41'));
    if (v('kakutei00') > 0) {
      set('kakutei02', Math.floor(v('kakutei00') * v('kakutei01')));
    } else {
      set('kakutei12', Math.floor(v('kakutei10') * v('kakutei11')));
      set('kakutei42', Math.floor(v('kakutei40') * v('kakutei41')));
      set('kakutei02', Math.floor(v('kakutei12') + v('kakutei42')));
    }

    set('kakutei52', Math.floor(v('kakutei50') * v('kakutei51')));

    set('gaisan01', v('gaisan11') + v('gaisan41'));
    if (this.el('gaisan10')?.value !== this.el('gaisan40')?.value) {
      if (this.el('gaisan10')?.value === '') {
        set('gaisan12', Math.floor(v('gaisan00') * v('gaisan11')));
      } else {
        set('gaisan12', Math.floor(v('gaisan10') * v('gaisan11')));
      }
      set('gaisan42', Math.floor(v('gaisan40') * v('gaisan41')));
    }

    if (v('gaisan00') > 0) {
      set('gaisan02', Math.floor(v('gaisan00') * v('gaisan01')));
    } else {
      set('gaisan02', Math.floor(v('gaisan12') + v('gaisan42')));
    }

    // 充当額、不足額
    const val1 = v('hokenryo') - v('kakutei02');
    if (val1 === 0) {
      set('sashihiki_juto', 0);
      set('sashihiki_fusoku', 0);
    } else if (val1 > 0) {
      set('sashihiki_juto', Math.floor(val1));
      set('sashihiki_fusoku', 0);
    } else {
      set('sashihiki_juto', 0);
      set('sashihiki_fusoku', -1 * Math.floor(val1));
    }

    const val2 = v('gaisan02');
    if (val2 < 400000) {
      set('kibetsu00', val2);
      set('kibetsu10', 0);
      set('kibetsu20', 0);
      set('ennou', 1);
      const opt3 = this.el('ennou')?.querySelector('option[value="3"]');
      if (opt3) opt3.disabled = true;
    } else {
      const opt3 = this.el('ennou')?.querySelector('option[value="3"]');
      if (opt3) opt3.disabled = false;
    }

    // 延納
    if (v('ennou') === 3) {
      const val3 = Math.floor(val2 / 3);
      set('kibetsu00', val2 - 2 * val3);
      set('kibetsu10', val3);
      set('kibetsu20', val3);
    }
    if (v('ennou') === 1) {
      set('kibetsu00', val2);
    }

    // 還付金 max("申告済概算保険料"-"確定労働保険料"-"概算労働保険料"-"一般拠出金",0)
    const val4 = v('hokenryo') - v('kakutei02') - v('gaisan02') - v('kakutei52');
    set('sashihiki_kanpu', val4 > 0 ? val4 : 0);

    // 充当額の配分
    let juto = v('sashihiki_juto');
    if (juto > 0) {
      set('kibetsu02', 0);
      if (juto > v('kibetsu00')) {
        set('kibetsu01', v('kibetsu00'));
        juto -= v('kibetsu00');
      } else {
        set('kibetsu01', juto);
        juto = 0;
      }
    }

    if (juto <= 0) {
      set('kibetsu04', 0);
    } else if (juto > v('kakutei52')) {
      set('kibetsu04', v('kakutei52'));
      juto -= v('kakutei52');
    } else {
      set('kibetsu04', juto);
      juto = 0;
    }

    if (juto <= 0) {
      set('kibetsu11', 0);
    } else if (juto > v('kibetsu10')) {
      set('kibetsu11', v('kibetsu10'));
      juto -= v('kibetsu10');
    } else {
      set('kibetsu11', juto);
      juto = 0;
    }

    if (juto <= 0) {
      set('kibetsu21', 0);
    } else if (juto > v('kibetsu00')) {
      set('kibetsu21', v('kibetsu20'));
      juto -= v('kibetsu20');
    } else {
      set('kibetsu21', juto);
      juto = 0;
    }

    // 不足額
    if (v('sashihiki_fusoku') > 0) {
      set('kibetsu01', 0);
      set('kibetsu02', v('sashihiki_fusoku'));
    }

    set('kibetsu03', v('kibetsu00') - v('kibetsu01') + v('kibetsu02'));
    set('kibetsu05', v('kakutei52') - v('kibetsu04'));
    set('kibetsu06', v('kibetsu03') + v('kibetsu05'));
    set('kibetsu12', v('kibetsu10') - v('kibetsu11'));
    set('kibetsu22', v('kibetsu20') - v('kibetsu21'));

    set('receipt00', v('kibetsu03'));
    set('receipt01', v('kibetsu05'));
    set('receipt02', v('kibetsu06'));

    if (v('ennou') === 1) {
      ['kibetsu10', 'kibetsu11', 'kibetsu12', 'kibetsu20', 'kibetsu21', 'kibetsu22'].forEach((id) =>
        set(id, '')
      );
    }

    this.persist();
    this.element.querySelectorAll('input[type=text]').forEach((el) => {
      el.value = this.numberWithCommas(el.value);
    });
  }

  persist() {
    // 旧 remote:true フォームの $.submit 相当。レスポンス("true"/"false")は参照しない。
    fetch(this.element.action, {
      method: 'POST',
      body: new FormData(this.element),
      credentials: 'same-origin',
      headers: { Accept: 'text/plain' },
    }).catch(() => {});
  }

  numberWithCommas(x) {
    if (x === '') return '';
    x = x.toString();
    x = x.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 65248));
    x = x.replace(/．/, '.').replace(/[^0-9.]/g, '');
    x = x.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (x.match(/\.$/)) return x;
    const xs = x.split(/\./);
    if (xs[1]) {
      return `${xs[0]}.${xs[1].substring(0, 2)}`;
    }
    return xs[0];
  }
}
